import React, { FC } from "react";
import Navbar from "../../components/QuoteForm/Navbar";
import {
  Divider,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import AdminDealerSearch from "../../components/DealerSearch/AdminDealerSearch";
import Header from "../../components/QuoteForm/Header";
import RequiredMsgs from "../../components/QuoteForm/RequiredMsgs";
import MatchingTaxResults from "../../components/QuoteForm/MatchingTaxResults";
import DealerSalesTax from "../../components/QuoteForm/DealerSalesTax";
import DeliveryInfo from "../../components/QuoteForm/DeliveryInfo";
import ProjectName from "../../components/QuoteForm/ProjectName";
import DealerInfo from "../../components/QuoteForm/DealerInfo";
import CustomerInfo from "../../components/QuoteForm/CustomerInfo";
import DeliveryChips from "../../components/QuoteForm/DeliveryChips";
import CreateQuoteBtn from "../../components/QuoteForm/CreateQuoteBtn";
import UpdateQuoteBtn from "../../components/QuoteForm/UpdateQuoteBtn";
import Copyright from "../../components/Copyright";
import CustomerHeard from "../../components/QuoteForm/CustomerHeard";
import { blueGrey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import useQuoteFormSelector from "./useQuoteFormSelector";
import dealerRoles from "../../util/dealerAccounts";

const StyledDiv = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  marginLeft: 150,
  marginRight: 150,
  flexWrap: "wrap",
  backgroundColor: "white",
  [theme.breakpoints.down("lg")]: {
    marginLeft: 0,
    marginRight: 0,
  },
}));

const StyledDivWrap = styled("div")({
  backgroundColor: blueGrey[50],
  // height: "100%",
  minHeight: "100vh",
  position: "relative",
});

const StyledDivTitle = styled("div")({
  display: "flex",
  justifyContent: "flex-start",
  marginTop: 80,
  paddingLeft: 20,
  borderBottom: "2px solid grey",
  paddingBottom: 5,
});

const StyledTypography = styled(Typography)({
  position: "absolute",
  right: 40,
  top: 20,
  color: "#4b4b4b",
});

const StyledForm = styled("form")(({ theme }) => ({
  paddingTop: 30,
  paddingBottom: 30,
  width: "100%",
  "& > *": {
    margin: theme.spacing(1),
    width: "25ch",
  },
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  position: "relative",
}));

const StyledDivCol = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  width: "100%",
});

const StyledCircularProgress = styled(CircularProgress)({
  position: "fixed",
  left: "50%",
  top: "50%",
});

const QuoteForm: FC = () => {
  const {
    handleTaxChange,
    tax,
    adminFillFields,
    handleClose,
    handleOpen,
    open,
    returnToConfig,
    handleUpdateQuote,
    handleCreateQuote,
    taxResults,
    isBtnDisabled,
    handleReturn,
    handleDelete,
    salesTaxErr,
    handleCheck,
    handleAllChange,
    requireds,
    state,
    err,
    data,
    isFetching,
    isLoadingUser,
    userError,
    userData,
    isFetchingUser,
    isQuoteCreated,
    location,
    hideCurrentTax,
    handleAdress,
    addressErr,
    setAddressErr
  } = useQuoteFormSelector();

  if (userError) {
    return <div>An Error occured, please try refreshing the page.</div>;
  }

  if (isFetchingUser || isLoadingUser || isFetching) {
    return (
      <Backdrop open={isFetchingUser || isLoadingUser}>
        <StyledCircularProgress size={60} color="primary" />
      </Backdrop>
    );
  }

  return (
    <StyledDivWrap>
      <Navbar
        goBackFunction={handleReturn}
        btnText="Return to Dealer Zone"
        quoteNumber={data && data.quoteNumber ? data.quoteNumber : null}
        returnedPath="form"
        historyState={location?.state || null}
      />

      {userData.signInRole !== "Dealer" && !isQuoteCreated && (
        <AdminDealerSearch
          role={userData.signInRole}
          open={open}
          handleClose={handleClose}
          adminFillFields={adminFillFields}
        />
      )}

      <StyledDivTitle>
        <Typography style={{ fontWeight: "bold" }} variant="h3">
          Quote Information
        </Typography>
      </StyledDivTitle>

      <StyledDiv style={{ position: "relative" }}>
        {!isFetching && data && data.createdBy && (
          <StyledTypography variant="subtitle2">
            Created By: <span>{data.createdBy}</span>
          </StyledTypography>
        )}

        <StyledForm noValidate autoComplete="off">
          <Header text="Job Details" />

          <ProjectName
            handleAllChange={handleAllChange}
            role={userData.role}
            signedInUserRole={userData.signInRole}
            jobName={state.jobName}
            formNotes={state.formNotes}
            agSalesperson={state.agSalesperson}
            leadNumber={state.leadNumber}
          />

          <Header text="Dealer Information" />

          <DealerInfo
            role={userData.role}
            signInRole={userData.signInRole}
            handleAllChange={handleAllChange}
            state={state}
            handleOpen={handleOpen}
            isQuoteCreated={isQuoteCreated}
          />

          <Header text="Customer Information" />

          <CustomerInfo
            handleAllChange={handleAllChange}
            signInRole={userData.signInRole}
            customerName={state.customerName}
            customerEmail={state.customerEmail}
            customerPhone={state.customerPhone}
          />

          <CustomerHeard
            handleChange={handleAllChange}
            heardAbout={state.heardAbout}
            heardAboutOther={state.heardAboutOther}
          />

          <Header text="Shipping Information" />

          <StyledDivCol>
            {state.checkedJobsite && (
              <>
                <Typography variant="caption">* Upcharge Applies</Typography>
                <Typography variant="caption">
                  * Two-person minimum for hand unloading on Jobsite deliveries.
                </Typography>
                <Typography
                  style={{ paddingBottom: 20, textAlign: "center" }}
                  variant="caption"
                >
                  * Some areas may be outside our delivery zone; call (805)
                  644-4494 if unsure.
                </Typography>
              </>
            )}

            <DeliveryChips
              checkedDealer={state.checkedDealer}
              checkedJobsite={state.checkedJobsite}
              handleCheck={handleCheck}
              handleDelete={handleDelete}
            />

            {state.checkedJobsite && (
              <>
                <Typography style={{ padding: 20 }}>
                  Jobsite address can be entered on the Order Summary page prior
                  to sign off.
                </Typography>
                <Divider style={{ width: "50%", backgroundColor: "black" }} />
              </>
            )}

            {state.checkedDealer && (
              <DeliveryInfo
                handleAddress={handleAdress}
                address={state.address}
                city={state.city}
                zip={state.zip}
                state={state.state || ""}
                dealerDelivery={dealerRoles.includes(userData.role)}
                signInRole={userData.signInRole || ""}
                addressErr={addressErr}
                setAddressErr={setAddressErr}
              />
            )}

            {(state.checkedDealer || state.checkedJobsite) &&
              !userData.dealerTax && (
                <>
                  <DealerSalesTax
                    handleAllChange={handleAllChange}
                    customerCity={state.customerCity}
                    customerZip={state.customerZip}
                    currentSalesTax={
                      isQuoteCreated &&
                      !hideCurrentTax &&
                      !taxResults.length &&
                      !isFetching &&
                      data.salesTax
                        ? data.salesTax
                        : null
                    }
                  />

                  {taxResults.length ? (
                    <MatchingTaxResults
                      tax={tax}
                      handleTaxChange={handleTaxChange}
                      taxResults={taxResults}
                    />
                  ) : null}

                  {salesTaxErr && (
                    <Typography style={{ color: "red" }}>
                      {salesTaxErr}
                    </Typography>
                  )}
                </>
              )}
          </StyledDivCol>
        </StyledForm>
        {!isQuoteCreated ? (
          <CreateQuoteBtn
            isBtnDisabled={isBtnDisabled || addressErr}
            handleCreateQuote={handleCreateQuote}
          />
        ) : (
          <UpdateQuoteBtn
            isBtnDisabled={isBtnDisabled || addressErr}
            handleUpdateQuote={handleUpdateQuote}
            returnToConfig={returnToConfig}
          />
        )}
        {err && requireds.length ? (
          <RequiredMsgs requireds={requireds} />
        ) : null}
      </StyledDiv>

      <Copyright position="right" />
    </StyledDivWrap>
  );
};

export default QuoteForm;
